import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { useMe, useLogout } from "@/lib/auth";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/lib/i18n";
import { Avatar } from "@/components/Avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const CANCELLATION_REASONS = [
  "Too expensive",
  "Not useful",
  "Technical issues",
  "Other",
];

export function CancelSubscriptionDialog({ userEmail }: { userEmail: string }) {
  const [open, setOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReason) {
      toast.error("Please select a reason for cancellation.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch("https://kidology.app/api/subscription/cancel", {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: userEmail,
          reason: `(Courses) ${selectedReason}`,
          message: message,
        }),
      });

      if (response.ok || response.type === "opaque") {
        setIsSubmitted(true);
        toast.success("Subscription cancellation request sent.");
      } else {
        throw new Error("Failed to submit request");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to submit request. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setSelectedReason(null);
      setMessage("");
      setIsSubmitted(false);
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={(val) => { if (!val) handleClose(); else setOpen(val); }}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors focus:outline-hidden"
        >
          Cancel subscription
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-xl text-slate-900 font-semibold text-center sm:text-left">
            Cancel Subscription
          </DialogTitle>
        </DialogHeader>

        {isSubmitted ? (
          <div className="flex flex-col items-center justify-center text-center py-6 gap-4 animate-in fade-in-50 duration-200">
            <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-slate-900">Request Submitted</h3>
            <p className="text-sm text-slate-500 max-w-xs leading-relaxed">
              We've received your cancellation request. Our team will process it shortly.
            </p>
            <Button type="button" className="mt-4" onClick={handleClose}>
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 pt-2">
            <div className="space-y-3">
              <Label className="text-sm font-medium text-slate-700">Why are you leaving?</Label>
              <div className="grid grid-cols-1 gap-2.5">
                {CANCELLATION_REASONS.map((reason) => {
                  const isSelected = selectedReason === reason;
                  return (
                    <button
                      key={reason}
                      type="button"
                      onClick={() => setSelectedReason(reason)}
                      className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg border text-left text-sm transition-all focus:outline-hidden ${
                        isSelected
                          ? "border-slate-900 bg-slate-50 font-medium text-slate-900"
                          : "border-slate-200 hover:border-slate-300 text-slate-700 bg-white"
                      }`}
                    >
                      <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                        isSelected ? "border-slate-900" : "border-slate-300"
                      }`}>
                        {isSelected && <span className="w-2 h-2 rounded-full bg-slate-900 animate-in zoom-in-50 duration-100" />}
                      </span>
                      {reason}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cancellation-message" className="text-sm font-medium text-slate-700">
                Anything else you want to tell us? (optional)
              </Label>
              <Textarea
                id="cancellation-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message here..."
                className="resize-none min-h-[90px] border-slate-200 focus:border-slate-400 focus:ring-0 focus-visible:ring-0"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5 pt-2">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={isSubmitting || !selectedReason}>
                {isSubmitting ? "Submitting..." : "Submit Request"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

import { Card } from "@/components/ui/card";

/**
 * Profile route — radical-minimum shape per
 * Skill("sketch-findings-teachable_clone") → references/post-auth-pages.md
 * → "Profile page". Avatar + email + Sign-out only.
 * Logout wiring preserved from Phase 2 plan 02-03 (useLogout + toast +
 * replace-navigate to /login). PublicShell (App.tsx) supplies the
 * surrounding MinimalNav/MinimalFooter chrome.
 */
import { getTenantConfig } from "@/lib/tenant";

export function Profile() {
  const tenant = getTenantConfig();
  const { t } = useTranslation();
  useDocumentMeta({
    title: `${t("profile_title")} · ${tenant.brandName}`,
    description: t("profile_details"),
  });


  const { data: me, isLoading, isError } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();

  async function handleLogout() {
    try {
      await logout.mutateAsync();
      toast.success(t("profile_logged_out"));
      navigate("/login", { replace: true });
    } catch {
      toast.error(t("profile_logout_failed"));
    }
  }

  if (isLoading || !me) {
    return (
      <div
        aria-busy="true"
        className="mx-auto flex max-w-[760px] flex-col items-center gap-6 px-4 py-12 lg:px-6"
      >
        <p className="text-sm text-muted-foreground">{t("profile_loading")}</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto flex max-w-[760px] flex-col items-center gap-6 px-4 py-12 lg:px-6">
        <p className="text-sm text-muted-foreground">
          Something went wrong on our end. Try again in a moment.
        </p>
      </div>
    );
  }

  const initial = me.email ? me.email.charAt(0).toUpperCase() : "?";

  return (
    <div className="mx-auto flex max-w-[400px] flex-col items-center px-4 py-12 lg:px-6">
      <Card className="w-full p-8 flex flex-col items-center text-center gap-6 border-none shadow-none bg-transparent">
        <Avatar size="lg" fallback={initial} alt="" />
        <p className="text-base font-medium text-slate-700">{me.email}</p>
        {me.hasActiveSubscription && (
          <p className="text-xs text-muted-foreground font-medium -mt-4">
            {t("profile_active_sub_desc")}
          </p>
        )}


        <div className="w-full flex flex-col gap-4 mt-2">
          <Button
            type="button"
            variant="danger"
            size="touch"
            className="w-full"
            onClick={handleLogout}
            disabled={logout.isPending}
          >
            {logout.isPending ? t("profile_signing_out") : t("profile_sign_out")}
          </Button>
          {/* <CancelSubscriptionDialog userEmail={me.email} /> */}
        </div>
      </Card>
    </div>
  );
}
